import React from "react";
import { Modal } from "react-responsive-modal";
import "react-responsive-modal/styles.css";

/**
 * ConfirmModal - Reusable confirmation dialog
 * Matches SPONSA design system colors and typography
 *
 * @param {boolean} open - Modal visibility state
 * @param {function} onClose - Callback when modal closes
 * @param {function} onConfirm - Callback when confirm button clicked
 * @param {string} title - Modal title (default: "Are you sure?")
 * @param {string} description - Modal description text
 * @param {string} confirmText - Confirm button text (default: "Disconnect")
 * @param {string} cancelText - Cancel button text (default: "Cancel")
 * @param {boolean} isDanger - Use danger styling for confirm button
 */
function ConfirmModal({
  open,
  onClose,
  onConfirm,
  title = "Are you sure?",
  description = "This action cannot be undone.",
  confirmText = "Disconnect",
  cancelText = "Cancel",
  isDanger = true,
}) {
  return (
    <Modal
      open={open}
      onClose={onClose}
      center
      classNames={{
        modal: "sp-confirm-modal",
        overlay: "sp-confirm-overlay",
        closeButton: "sp-confirm-close",
      }}
      closeOnEsc
      closeOnOverlayClick
      showCloseIcon={false}
    >
      <div className="sp-confirm-content">
        {/* Icon */}
        <div className="sp-confirm-icon">
          <svg
            className="sp-confirm-icon-svg"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
        </div>

        {/* Content */}
        <div className="sp-confirm-body">
          <h3 className="sp-confirm-title">{title}</h3>
          <p className="sp-confirm-description">{description}</p>
        </div>

        {/* Actions */}
        <div className="sp-confirm-actions">
          <button
            type="button"
            className="sp-confirm-btn sp-confirm-btn-cancel"
            onClick={onClose}
          >
            {cancelText}
          </button>
          <button
            type="button"
            className={`sp-confirm-btn sp-confirm-btn-confirm${
              isDanger ? " sp-confirm-btn-danger" : ""
            }`}
            onClick={() => {
              onConfirm?.();
              onClose();
            }}
          >
            {confirmText}
          </button>
        </div>
      </div>

      {/* Inline styles matching SPONSA design system */}
      <style>{`
        /* Overlay */
        .sp-confirm-overlay {
          background: rgba(3, 0, 33, 0.85) !important;
          backdrop-filter: blur(8px);
          -webkit-backdrop-filter: blur(8px);
        }

        /* Modal container */
        .sp-confirm-modal {
          background: rgba(255, 255, 255, 0.03) !important;
          border: 1px solid rgba(255, 255, 255, 0.08) !important;
          border-radius: 20px !important;
          padding: 24px !important;
          max-width: 400px !important;
          box-shadow: 0 25px 80px -20px rgba(0, 0, 0, 0.6) !important;
        }

        /* Close button (hidden but accessible) */
        .sp-confirm-close {
          display: none !important;
        }

        /* Content wrapper */
        .sp-confirm-content {
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
          gap: 20px;
        }

        /* Warning icon */
        .sp-confirm-icon {
          width: 56px;
          height: 56px;
          border-radius: 16px;
          background: rgba(239, 68, 68, 0.1);
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }

        .sp-confirm-icon-svg {
          width: 28px;
          height: 28px;
          color: #ef4444;
        }

        /* Body text */
        .sp-confirm-body {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .sp-confirm-title {
          font-size: 18px;
          font-weight: 700;
          color: #ffffff;
          margin: 0;
          line-height: 1.3;
        }

        .sp-confirm-description {
          font-size: 14px;
          font-weight: 400;
          color: rgba(255, 255, 255, 0.6);
          margin: 0;
          line-height: 1.5;
        }

        /* Action buttons */
        .sp-confirm-actions {
          display: flex;
          gap: 12px;
          width: 100%;
          margin-top: 8px;
        }

        .sp-confirm-btn {
          flex: 1;
          padding: 12px 16px;
          border-radius: 12px;
          font-size: 14px;
          font-weight: 600;
          font-family: inherit;
          cursor: pointer;
          transition: all 0.15s ease;
          border: none;
          outline: none;
        }

        /* Cancel button */
        .sp-confirm-btn-cancel {
          background: rgba(255, 255, 255, 0.04);
          color: rgba(255, 255, 255, 0.8);
          border: 1px solid rgba(255, 255, 255, 0.08);
        }

        .sp-confirm-btn-cancel:hover {
          background: rgba(255, 255, 255, 0.08);
          color: #ffffff;
        }

        .sp-confirm-btn-cancel:active {
          transform: scale(0.98);
        }

        /* Confirm button (default danger style) */
        .sp-confirm-btn-confirm {
          background: #ef4444;
          color: #ffffff;
        }

        .sp-confirm-btn-confirm:hover {
          background: #dc2626;
          box-shadow: 0 4px 20px rgba(239, 68, 68, 0.4);
        }

        .sp-confirm-btn-confirm:active {
          transform: scale(0.98);
        }

        /* Non-danger variant (e.g., for "Save" actions) */
        .sp-confirm-btn-confirm:not(.sp-confirm-btn-danger) {
          background: #00FF8F;
          color: #030021;
        }

        .sp-confirm-btn-confirm:not(.sp-confirm-btn-danger):hover {
          background: #00e680;
          box-shadow: 0 4px 20px rgba(0, 255, 143, 0.3);
        }

        /* Responsive adjustments */
        @media (max-width: 480px) {
          .sp-confirm-modal {
            margin: 16px !important;
            padding: 20px !important;
          }
          
          .sp-confirm-actions {
            flex-direction: column-reverse;
          }
          
          .sp-confirm-btn {
            padding: 14px 16px;
          }
        }
      `}</style>
    </Modal>
  );
}

export default ConfirmModal;
